import React, { useState } from "react";
import {
  ProgressionTemplate,
  ProgressionContainer,
} from "../../../libs/progression/types";
import { EXERCISE_TYPES } from "../../../libs/exercises/types";
import { Exercise } from "../../../libs/exercises/types";
import { FolderBrowser } from "./FolderBrowser";

type Props = {
  progressionTemplates: ProgressionTemplate[];
  progressionContainers: ProgressionContainer[];
  exerciseType: string;
  exercises: Exercise[];
  // DEFAULT: single selection
  selectedProgressionId: string | null;
  onSelectProgression: (id: string | null) => void;
  // SUPERSET: per-exercise mapping
  supersetExercises: { id: string; progressionId: string | null }[];
  onUpdateSupersetProgression: (exerciseId: string, progressionId: string | null) => void;
};

export const StepProgressionPicker: React.FC<Props> = ({
  progressionTemplates,
  progressionContainers,
  exerciseType,
  exercises,
  selectedProgressionId,
  onSelectProgression,
  supersetExercises,
  onUpdateSupersetProgression,
}) => {
  const isSuperset = exerciseType === EXERCISE_TYPES.SUPERSET;
  const [activeSupersetIdx, setActiveSupersetIdx] = useState(0);

  const selectedTemplate = selectedProgressionId
    ? progressionTemplates.find((t) => t.id + "" === selectedProgressionId)
    : null;

  const renderProgressionCard = (
    template: ProgressionTemplate,
    isSelected: boolean,
    onSelect: () => void
  ) => (
    <button
      onClick={onSelect}
      className={`flex flex-col gap-2 p-4 rounded-xl border-2 transition-all text-left w-full ${
        isSelected
          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-sm"
          : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={`text-sm font-medium ${
            isSelected
              ? "text-blue-700 dark:text-blue-300"
              : "text-gray-900 dark:text-white"
          }`}
        >
          {template.name}
        </p>
        {isSelected && (
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        {template.numWeeks} settiman{template.numWeeks !== 1 ? "e" : "a"} &middot;{" "}
        {template.weeks[0]?.sets.length ?? 0} set
      </p>
      {/* Mini week bars */}
      <div className="flex gap-1 mt-1">
        {template.weeks.slice(0, 8).map((w, i) => (
          <div
            key={i}
            className="flex-1 h-1.5 rounded-full bg-blue-200 dark:bg-blue-800"
            title={`Settimana ${w.weekNumber}`}
          />
        ))}
        {template.weeks.length > 8 && (
          <span className="text-[10px] text-gray-400">+{template.weeks.length - 8}</span>
        )}
      </div>
    </button>
  );

  // DEFAULT: single progression picker with "skip" button
  if (!isSuperset) {
    return (
      <div className="h-full flex flex-col">
        {/* Skip button */}
        <div className="mb-3">
          <button
            onClick={() => onSelectProgression(null)}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              selectedProgressionId == null
                ? "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium"
                : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            Nessuna progressione
          </button>
        </div>

        {/* Progression preview */}
        {selectedTemplate && (
          <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              {selectedTemplate.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Le settimane verranno pre-compilate con questa progressione.
            </p>
          </div>
        )}

        <div className="flex-1 min-h-0">
          <FolderBrowser
            containers={progressionContainers}
            items={progressionTemplates}
            getContainerId={(c) => c.id}
            getContainerName={(c) => c.name}
            getContainerColor={(c) => c.color}
            getContainerParentId={() => null}
            getItemId={(t) => t.id + ""}
            getItemName={(t) => t.name}
            getItemContainerId={(t) =>
              t.idContainer != null ? t.idContainer : null
            }
            matchesSearch={(t, q) => t.name.toLowerCase().includes(q)}
            hierarchical={false}
            selectedItemId={selectedProgressionId}
            onSelectItem={(id) => onSelectProgression(id)}
            renderItemCard={renderProgressionCard}
            searchPlaceholder="Cerca progressione..."
            emptyMessage="Nessuna progressione disponibile."
          />
        </div>
      </div>
    );
  }

  // SUPERSET: per-exercise progression mapping
  const activeExercise = supersetExercises[activeSupersetIdx];
  const activeEx = activeExercise
    ? exercises.find((e) => e.id === activeExercise.id)
    : null;

  return (
    <div className="h-full flex gap-4">
      {/* Exercise list (left) */}
      <div className="w-56 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-600 pr-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Assegna progressione
        </h3>
        <div className="flex flex-col gap-2">
          {supersetExercises.map((se, i) => {
            const ex = exercises.find((e) => e.id === se.id);
            const hasProgression = se.progressionId != null;
            const active = i === activeSupersetIdx;
            return (
              <button
                key={se.id}
                onClick={() => setActiveSupersetIdx(i)}
                className={`flex items-center gap-2 p-2.5 rounded-lg text-left transition-colors ${
                  active
                    ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700"
                    : "border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                    {ex?.name ?? se.id}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {hasProgression
                      ? progressionTemplates.find(
                          (t) => t.id + "" === se.progressionId
                        )?.name ?? "Progressione"
                      : "Nessuna progressione"}
                  </p>
                </div>
                {hasProgression && (
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progression browser (right) */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="mb-2 flex items-center gap-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Progressione per:{" "}
            <span className="font-medium text-gray-800 dark:text-gray-200">
              {activeEx?.name ?? "—"}
            </span>
          </p>
          {activeExercise?.progressionId && (
            <button
              onClick={() =>
                onUpdateSupersetProgression(activeExercise.id, null)
              }
              className="text-xs text-red-500 hover:text-red-600"
            >
              Rimuovi
            </button>
          )}
        </div>

        <button
          onClick={() =>
            activeExercise &&
            onUpdateSupersetProgression(activeExercise.id, null)
          }
          className={`mb-3 px-4 py-2 text-sm rounded-lg transition-colors self-start ${
            activeExercise?.progressionId == null
              ? "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium"
              : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
          }`}
        >
          Nessuna progressione
        </button>

        <div className="flex-1 min-h-0">
          <FolderBrowser
            containers={progressionContainers}
            items={progressionTemplates}
            getContainerId={(c) => c.id}
            getContainerName={(c) => c.name}
            getContainerColor={(c) => c.color}
            getContainerParentId={() => null}
            getItemId={(t) => t.id + ""}
            getItemName={(t) => t.name}
            getItemContainerId={(t) =>
              t.idContainer != null ? t.idContainer : null
            }
            matchesSearch={(t, q) => t.name.toLowerCase().includes(q)}
            hierarchical={false}
            selectedItemId={activeExercise?.progressionId ?? null}
            onSelectItem={(id) =>
              activeExercise &&
              onUpdateSupersetProgression(activeExercise.id, id)
            }
            renderItemCard={renderProgressionCard}
            searchPlaceholder="Cerca progressione..."
            emptyMessage="Nessuna progressione disponibile."
          />
        </div>
      </div>
    </div>
  );
};
